import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconElements } from '../../../../config/icons';
import { IntervalType } from '../../../../types/IntervalType';
import { Button } from '../../../general/Button';

export const CustomIntervalPicker: React.FC<{
    isCustomPickerShown: boolean;
    setIsCustomPickerShown: Function;
    onIntervalChange: (interval: IntervalType, custom?: { endDate: DateTime; startDate: DateTime }) => void;
}> = ({ isCustomPickerShown, setIsCustomPickerShown, onIntervalChange }) => {
    const [isStartDateCorrect, setIsStartDateCorrect] = useState<string | null>(null);
    const [isEndDateCorrect, setIsEndDateCorrect] = useState<string | null>(null);
    const [customTemporaryStartDate, setCustomTemporaryStartDate] = useState('');
    const [customTemporaryEndDate, setCustomTemporaryEndDate] = useState('');
    const { t } = useTranslation('funds');
    useEffect(() => {
        if (customTemporaryEndDate.valueOf() && customTemporaryStartDate.valueOf()) {
            const minimalDate = '01-01-2005';
            const minimalDateTime = DateTime.fromFormat(minimalDate, 'dd-MM-yyyy');
            const startDate = DateTime.fromFormat(customTemporaryStartDate, 'yyyyy-MM-dd');
            const endDate = DateTime.fromFormat(customTemporaryEndDate, 'yyyyy-MM-dd');
            if (startDate >= minimalDateTime) {
                setIsStartDateCorrect(null);
            } else {
                setIsStartDateCorrect(`* Startdatum is vroeger dan ${minimalDate}`);
            }
            if (endDate <= startDate) {
                setIsEndDateCorrect(`* Einddatum is voor of tegelijk met startdatum`);
            } else if (endDate > DateTime.now()) {
                setIsEndDateCorrect(`* Einddatum is in de toekomst`);
            } else {
                setIsEndDateCorrect(null);
            }
        } else if (customTemporaryStartDate.valueOf()) {
            setIsEndDateCorrect('* Einddatum nog niet volledig ingevuld');
            setIsStartDateCorrect(null);
        } else {
            setIsStartDateCorrect('* Startdatum is nog niet volledig ingevuld');
        }
        return;
    }, [customTemporaryStartDate, customTemporaryEndDate]);

    return (
        <div
            className={`absolute w-64 md:w-auto z-30 flex top-full inset-x-2 border-2 border-gray-300 bg-white rounded-md mt-1 transition-opacity duration-500 ${
                isCustomPickerShown ? 'opacity-100' : 'opacity-0 hidden'
            }`}
        >
            <div className="text-sm md:text-base flex flex-col flex-1">
                <span className="flex flex-row justify-between items-center pr-3 pt-2 pb-4">
                    <span className="ml-3">{t('funds-comparison-screen-custom-interval-main-title')}</span>
                    <div
                        onClick={() => {
                            setIsCustomPickerShown(false);
                        }}
                    >
                        <IconElements.Cross className="text-bnd-green w-3 h-3 cursor-pointer" />
                    </div>
                </span>
                <span className="pb-2 flex flew-row justify-between">
                    <span className="ml-3">{t('funds-comparison-screen-custom-interval-start-date-title')}</span>
                    <div className="relative mb-8">
                        <input
                            className={`font-medium w-36 md:w-40 absolute right-0 mr-3 border border-gray-300 rounded py-0.5 px-1 focus:outline-hidden ${
                                isStartDateCorrect === null &&
                                customTemporaryEndDate.valueOf() &&
                                customTemporaryStartDate.valueOf()
                                    ? 'ring-2 ring-bnd-green ring-opacity-20'
                                    : customTemporaryEndDate.valueOf() && customTemporaryStartDate.valueOf()
                                    ? 'ring-2 ring-bnd-pink ring-opacity-20'
                                    : ''
                            }`}
                            type="Date"
                            onChange={(event) => {
                                setCustomTemporaryStartDate(event.target.value);
                            }}
                        ></input>
                        <IconElements.Calendar className="absolute right-5 top-0.5 md:top-1" />
                    </div>
                </span>
                <span className="pb-4 flex flew-row justify-between">
                    <span className="ml-3">{t('funds-comparison-screen-custom-interval-end-date-title')}</span>
                    <div className="relative mb-8">
                        <input
                            className={`font-medium w-36 md:w-40 absolute right-0 mr-3 border border-gray-300 rounded py-0.5 px-1 focus:outline-hidden ${
                                isEndDateCorrect === null &&
                                customTemporaryEndDate.valueOf() &&
                                customTemporaryStartDate.valueOf()
                                    ? 'ring-2 ring-bnd-green ring-opacity-20'
                                    : customTemporaryEndDate.valueOf() && customTemporaryStartDate.valueOf()
                                    ? 'ring-2 ring-bnd-pink ring-opacity-20'
                                    : ''
                            } `}
                            type="Date"
                            onChange={(event) => {
                                setCustomTemporaryEndDate(event.target.value);
                            }}
                        ></input>
                        <IconElements.Calendar className="absolute right-5 top-0.5 md:top-1" />
                    </div>
                </span>
                <div className="pb-4 flex flex-row justify-between mr-3 items-center">
                    <span className="ml-3 mr-6 text-xs">
                        {isStartDateCorrect === null
                            ? isEndDateCorrect === null
                                ? ''
                                : isEndDateCorrect
                            : isStartDateCorrect}
                    </span>
                    <div>
                        <Button
                            buttonType={'secondary'}
                            isDisabled={!(isStartDateCorrect === null && isEndDateCorrect === null)}
                            onClick={() => {
                                setIsCustomPickerShown(false);
                                onIntervalChange('Custom', {
                                    startDate: DateTime.fromFormat(customTemporaryStartDate, 'yyyyy-MM-dd'),
                                    endDate: DateTime.fromFormat(customTemporaryEndDate, 'yyyyy-MM-dd'),
                                });
                            }}
                        >
                            {t('funds-comparison-screen-custom-interval-button')}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};
